import React, { useEffect, useState } from 'react';
import { FiCheck, FiClock, FiSend, FiX } from 'react-icons/fi';

interface ShellProps {
    icon: React.ReactNode;
    title: string;
    sub: string;
    note: string;
    onClose: () => void;
    buttons: React.ReactNode;
}

export const ModalShell: React.FC<ShellProps> = ({ icon, title, sub, note, onClose, buttons, children }) => {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return <div className="campaign-modal-overlay" onClick={onClose}>
        <div className="campaign-modal" role="dialog" aria-modal="true" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
                <span className="icon">{icon}</span>
                <div className="t"><strong>{title}</strong><span>{sub}</span></div>
                <button type="button" className="close" aria-label="Close" onClick={onClose}><FiX /></button>
            </div>
            <div className="modal-body">{children}</div>
            <div className="modal-footer">
                <span className="note">{note}</span>
                {buttons}
            </div>
        </div>
    </div>;
};

// spec 2:56692. Omitted: "Preview as" (merge-tag contact), Outlook/Gmail/iPhone screenshot previews.
export const SendTestModal: React.FC<{ defaultTo: string; sub?: string; hideAddTest?: boolean; onClose: () => void; onSend: (to: string, addTestToSubject: boolean) => Promise<boolean> }> = ({ defaultTo, sub, hideAddTest, onClose, onSend }) => {
    const [to, setTo] = useState(defaultTo);
    const [addTest, setAddTest] = useState(true);
    const [sending, setSending] = useState(false);
    const emails = to.split(/[,;\n]/).map(x => x.trim()).filter(Boolean);
    const valid = emails.length > 0 && emails.length <= 5;

    const send = async () => {
        setSending(true);
        const ok = await onSend(emails.join(', '), addTest);
        if (ok) onClose(); else setSending(false);
    };

    return <ModalShell icon={<FiSend />} title="Send a Test Email" sub={sub ?? "Check how it looks before it goes to investors"} note="Test sends don’t count towards reports" onClose={onClose}
        buttons={<>
            <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
            <button type="button" className="btn-primary" onClick={send} disabled={!valid || sending}><FiSend /> {sending ? 'Sending…' : 'Send Test'}</button>
        </>}>
        <label className="field">
            <span className="label">Send to <em>up to 5 addresses</em></span>
            <textarea rows={2} value={to} onChange={e => setTo(e.target.value)} />
            {emails.length > 5 && <span className="error">Up to 5 addresses</span>}
        </label>
        {!hideAddTest && <label className={`check-card${addTest ? ' on' : ''}`}>
            <input type="checkbox" checked={addTest} onChange={e => setAddTest(e.target.checked)} />
            <strong>Add “[TEST]” to the subject line</strong>
        </label>}
    </ModalShell>;
};

// spec 2:56733 with scheduling omitted: Send Now goes out immediately. Omitted: "Approved by", bounced-contacts line, cancel window.
export const SendCampaignModal: React.FC<{ subject: string; recipients: number; onClose: () => void; onSend: () => Promise<boolean> }> = ({ subject, recipients, onClose, onSend }) => {
    const [sending, setSending] = useState(false);

    const send = async () => {
        setSending(true);
        if (!await onSend()) setSending(false);
    };

    return <ModalShell icon={<FiClock />} title="Ready to Send?" sub={`“${subject}” to ${recipients.toLocaleString()} subscribed ${recipients === 1 ? 'contact' : 'contacts'}`}
        note="Goes out within a few minutes" onClose={onClose}
        buttons={<>
            <button type="button" className="btn-cancel" onClick={onClose}>Back</button>
            <button type="button" className="btn-primary" onClick={send} disabled={sending}><FiSend /> {sending ? 'Sending…' : 'Send Now'}</button>
        </>}>
        <ul className="checks">
            <li><FiCheck /> Preview sent and checked</li>
            <li><FiCheck /> Unsubscribe link present</li>
        </ul>
    </ModalShell>;
};
